/* Части формы экранов зоны: текстовый слот (§1.5 тип `text`: чипы, «Улучшить», «во весь экран», подпись под полем),
   шапка с ценой за единицу (§1.1 п. 1) и кнопка «Создать … · N C» с причиной недостающего (§1.6). */
import { useState, type ReactNode } from 'react';
import { uiText } from '../../../i18n';
import { OffButton } from '../../../ui/OffButton';
import { PromptChip } from '../../../ui/PromptChip';
import { TextEditor } from '../../../ui/TextEditor';
import { TextField } from '../../../ui/TextField';
import { PhotoImprove } from '../../photo/PhotoImprove';
import { StudioFormFoot } from '../../studio/StudioFormParts';

export function TextSlot({
  label,
  value,
  limit,
  disabled,
  chips = [],
  mode = 'append',
  note,
  improve = true,
  optional = true,
  onChange,
}: {
  label: string;
  value: string;
  limit: number;
  disabled: boolean;
  chips?: string[];
  /** append — чип дописывает, replace — заменяет текст (подпись коллажа) */
  mode?: 'append' | 'replace';
  note?: string;
  improve?: boolean;
  optional?: boolean;
  onChange(value: string): void;
}) {
  const [expanded, setExpanded] = useState(false);
  const has = (chip: string) =>
    mode === 'replace'
      ? value === chip
      : value
          .split(',')
          .map(part => part.trim())
          .includes(chip);
  const toggle = (chip: string) => {
    if (mode === 'replace') return onChange(value === chip ? '' : chip);
    const parts = value
      .split(',')
      .map(part => part.trim())
      .filter(Boolean);
    const next = has(chip) ? parts.filter(part => part !== chip) : [...parts, chip];
    const joined = next.join(', ');
    if (Array.from(joined).length <= limit) onChange(joined);
  };
  const chipRow = (editor = false) =>
    !!chips.length && (
      <div className={`chips${editor ? ' bc-edch' : ''}`}>
        {chips.map(chip => (
          <PromptChip
            key={chip}
            label={uiText(chip)}
            selected={has(chip)}
            disabled={disabled}
            onMouseDown={event => event.preventDefault()}
            onClick={() => toggle(chip)}
          />
        ))}
      </div>
    );
  const improver = improve && <PhotoImprove kind="photo" text={value} disabled={disabled} onPick={onChange} />;
  return (
    <div className="photo-field pf-ta shk-text">
      <TextField
        label={`${uiText(label)}${optional ? ` · ${uiText('необязательно')}` : ''}`}
        value={value}
        limit={limit}
        disabled={disabled}
        onChange={onChange}
        onExpand={() => setExpanded(true)}
        expandLabel={uiText('Во весь экран')}
      >
        {chipRow()}
        <div className="foot studio-description-actions">
          {!expanded && improver}
          {note && <span className="pf-fn">{uiText(note)}</span>}
        </div>
      </TextField>
      {expanded && (
        <TextEditor
          label={`${uiText(label)} · ${uiText('Ещё настройки')}`}
          value={value}
          limit={limit}
          disabled={disabled}
          onChange={onChange}
          onClose={() => setExpanded(false)}
          toolbar={
            <>
              {improver}
              {chipRow(true)}
            </>
          }
        />
      )}
    </div>
  );
}

export function ScreenHead({ title, unit, children }: { title: string; unit?: string | null; children?: ReactNode }) {
  return (
    <div className="shk-top bc-tt">
      <h2>{uiText(title)}</h2>
      {unit && <small className="shk-unit">{unit}</small>}
      {children}
    </div>
  );
}

export function CreateFoot({
  label,
  busy,
  blocked,
  pending,
  missing,
  available,
  onCreate,
}: {
  label: string;
  busy: boolean;
  blocked: boolean;
  pending: boolean;
  missing: string | null;
  available: boolean;
  onCreate(): void;
}) {
  return (
    <StudioFormFoot>
      <OffButton
        className="fin photo-submit"
        disabled={busy}
        label={uiText(busy ? 'Отправляем…' : pending ? 'Продолжить проверку' : label)}
        miss={
          blocked
            ? uiText('Проверяем доступность')
            : pending
              ? undefined
              : missing
                ? uiText(missing)
                : !available
                  ? uiText('Создание сейчас недоступно')
                  : undefined
        }
        onClick={() => {
          if (!busy && !blocked && (pending || (!missing && available))) onCreate();
        }}
      />
    </StudioFormFoot>
  );
}

/** Согласие на материалы: загрузка закрыта до отметки (use-studio-form.upload проверяет rights). */
export function RightsBox({ checked, disabled, text, onChange }: { checked: boolean; disabled: boolean; text: string; onChange(value: boolean): void }) {
  return (
    <label className="photo-consent">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={event => onChange(event.target.checked)} />
      {uiText(text)}
    </label>
  );
}
